import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Disc3, Loader2 } from "lucide-react";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1618609377864-68609b857e90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxkYXJrJTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWl4aW5nJTIwY29uc29sZXxlbnwwfHx8fDE3ODUzNzA4OTl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, body);
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden border-r border-border">
        <img src={AUTH_IMG} alt="mixing console" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="flex items-center gap-2 mb-4">
            <Disc3 className="h-6 w-6 text-primary" />
            <span className="font-heading font-extrabold text-lg tracking-tight">xyz Publishing OS</span>
          </div>
          <h2 className="font-heading text-4xl font-black tracking-tighter uppercase leading-none max-w-md">
            Own every split. Track every right.
          </h2>
          <p className="text-zinc-400 mt-4 max-w-md">
            The command center for songwriters and publishers — catalogs, ownership, PRO affiliations, ISRCs & ISWCs.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Disc3 className="h-6 w-6 text-primary" />
            <span className="font-heading font-extrabold text-lg">xyz Publishing OS</span>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter uppercase">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="text-zinc-400 mt-2 mb-8 text-sm">
            {mode === "login" ? "Access your publishing catalog." : "Start managing your catalog."}
          </p>

          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-secondary hover:bg-zinc-800 py-2.5 text-sm font-semibold transition-colors"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-zinc-500 uppercase tracking-widest">or</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-secondary border-border" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-secondary border-border" placeholder="you@label.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-secondary border-border" placeholder="••••••••" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <Button type="submit" data-testid="submit-auth-btn" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-md">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-zinc-500 mt-6">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-mode-btn"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
